import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  Send,
  MessageCircle,
  Loader,
  AlertTriangle,
  Flag,
  Edit2,
  Trash2,
  Crown,
  Star,
  MoreVertical
} from 'lucide-react';
import { showToast } from './Toast';
import { trackEvent } from '../utils/GoogleAnalytics';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';

/**
 * CommentsModal - Modal for viewing and posting comments
 * Features:
 * - View comments on a benchmark
 * - Post new comments (authenticated users)
 * - Edit comments (Pro/Premium only)
 * - Delete comments (owner/admin)
 * - Report comments
 * - User badges by tier
 */

// Badge component
function UserBadge({ tier }) {
  const badges = {
    premium: { icon: '👑', color: 'text-purple-600', bg: 'bg-purple-100', label: 'Premium' },
    pro: { icon: '⭐', color: 'text-blue-600', bg: 'bg-blue-100', label: 'Pro' },
    starter: { icon: '✨', color: 'text-green-600', bg: 'bg-green-100', label: 'Starter' }
  };

  const badge = badges[tier?.toLowerCase()];
  if (!badge) return null;

  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.color}`}>
      <span>{badge.icon}</span>
      <span className="hidden sm:inline">{badge.label}</span>
    </span>
  );
}

// Time formatter
function formatTime(dateString) {
  if (!dateString) return '';

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
}

// Single comment component
function CommentItem({ comment, user, onEdit, onDelete, onReport }) {
  const [showMenu, setShowMenu] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);
  const menuRef = useRef(null);

  const isOwner = user && comment.is_owner;
  const isAdmin = user?.is_admin;
  const canEdit = isOwner && ['premium', 'pro'].includes(user?.plan_tier?.toLowerCase());
  const canDelete = isOwner || isAdmin;

  // Close menu on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSaveEdit = () => {
    if (editContent.trim() && editContent !== comment.content) {
      onEdit(comment.id, editContent.trim());
    }
    setIsEditing(false);
  };

  return (
    <div className="py-3 border-b border-gray-100 last:border-0">
      <div className="flex items-start justify-between gap-2">
        {/* User info */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-gray-900">{comment.username}</span>
          <UserBadge tier={comment.user_tier} />
          <span className="text-xs text-gray-400">•</span>
          <span className="text-xs text-gray-400">{formatTime(comment.created_at)}</span>
          {comment.is_edited && (
            <span className="text-xs text-gray-400">(edited)</span>
          )}
        </div>

        {/* Actions menu */}
        {user && (
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
            >
              <MoreVertical size={16} />
            </button>

            {showMenu && (
              <div className="absolute right-0 top-8 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10 min-w-[140px]">
                {canEdit && (
                  <button
                    onClick={() => {
                      setIsEditing(true);
                      setShowMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Edit2 size={14} />
                    Edit
                  </button>
                )}
                {canDelete && (
                  <button
                    onClick={() => {
                      onDelete(comment.id);
                      setShowMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                  >
                    <Trash2 size={14} />
                    Delete
                  </button>
                )}
                {!isOwner && (
                  <button
                    onClick={() => {
                      onReport(comment.id);
                      setShowMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-orange-600 hover:bg-orange-50 flex items-center gap-2"
                  >
                    <Flag size={14} />
                    Report
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Comment content */}
      {isEditing ? (
        <div className="mt-2">
          <textarea
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            rows={3}
            autoFocus
          />
          <div className="flex gap-2 mt-2">
            <button
              onClick={handleSaveEdit}
              className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
            >
              Save
            </button>
            <button
              onClick={() => {
                setIsEditing(false);
                setEditContent(comment.content);
              }}
              className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <p className="mt-1.5 text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">
          {comment.content}
        </p>
      )}
    </div>
  );
}

// Report modal
function ReportModal({ isOpen, onClose, onSubmit }) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (reason.trim().length < 5) {
      showToast('Please provide a reason (at least 5 characters)', 'error');
      return;
    }
    setSubmitting(true);
    await onSubmit(reason.trim());
    setSubmitting(false);
    setReason('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl p-5 max-w-md w-full">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-orange-100 rounded-full">
            <Flag className="text-orange-600" size={20} />
          </div>
          <h3 className="font-bold text-gray-900">Report Comment</h3>
        </div>

        <p className="text-sm text-gray-600 mb-3">
          Why are you reporting this comment?
        </p>

        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Please describe the issue..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-200"
          rows={3}
        />

        <div className="flex gap-2 mt-4">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium disabled:opacity-50"
          >
            {submitting ? 'Submitting...' : 'Submit Report'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Main modal component
function CommentsModal({ isOpen, onClose, benchmarkId, benchmarkInfo, user, onLoginClick, onCommentAdded, onCommentDeleted }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [posting, setPosting] = useState(false);
  const [reportingId, setReportingId] = useState(null);
  const inputRef = useRef(null);

  // Load comments when modal opens
  useEffect(() => {
    if (isOpen && benchmarkId) {
      loadComments();
    }
  }, [isOpen, benchmarkId]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current && user) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, user]);

  const loadComments = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      const response = await fetch(`${API_URL}/comments/benchmark/${benchmarkId}`, { headers });

      if (response.ok) {
        const data = await response.json();
        setComments(data.comments || []);
      }
    } catch (error) {
      console.error('Failed to load comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePost = async () => {
    if (!newComment.trim()) return;
    if (!user) {
      onLoginClick?.();
      return;
    }

    const commentText = newComment.trim();

    // Optimistic update - add comment immediately (at the end for chronological order)
    const optimisticComment = {
      id: `temp-${Date.now()}`,
      username: user.name || user.email?.split('@')[0] || 'You',
      user_tier: user.plan_tier,
      content: commentText,
      created_at: new Date().toISOString(),
      is_owner: true,
      is_edited: false
    };

    setComments(prev => [...prev, optimisticComment]);
    setNewComment('');

    setPosting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/comments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          benchmark_id: benchmarkId,
          content: commentText
        })
      });

      if (response.ok) {
        // Silent refresh - no loading spinner
        const data = await response.json();
        if (data.comment_id) {
          // Replace temp comment with real one
          setComments(prev => prev.map(c =>
            c.id === optimisticComment.id
              ? { ...c, id: data.comment_id }
              : c
          ));
        }
        trackEvent('Comments', 'Post', benchmarkId);
        // ✅ Notify parent to update count
        if (onCommentAdded) onCommentAdded();
      } else {
        // Remove optimistic comment on error
        setComments(prev => prev.filter(c => c.id !== optimisticComment.id));
        setNewComment(commentText); // Restore the comment text so user doesn't lose it

        const data = await response.json();
        // Special handling for rate limit
        if (response.status === 429) {
          showToast('⏱️ Too many comments! Please wait a minute.', 'error');
        } else {
          showToast(data.detail || 'Failed to post comment', 'error');
        }
      }
    } catch (error) {
      // Remove optimistic comment on error
      setComments(prev => prev.filter(c => c.id !== optimisticComment.id));
      console.error('Error posting comment:', error);
      showToast('Failed to post comment', 'error');
    } finally {
      setPosting(false);
    }
  };

  const handleEdit = async (commentId, newContent) => {
    // Optimistic update - update immediately
    setComments(prev => prev.map(c =>
      c.id === commentId
        ? { ...c, content: newContent, is_edited: true }
        : c
    ));

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/comments/${commentId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content: newContent })
      });

      if (!response.ok) {
        // Revert on error - reload comments
        loadComments();
        const data = await response.json();
        showToast(data.detail || 'Failed to update', 'error');
      }
    } catch (error) {
      // Revert on error - reload comments
      loadComments();
      showToast('Failed to update comment', 'error');
    }
  };

  const handleDelete = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;

    // Optimistic update - remove immediately
    const deletedComment = comments.find(c => c.id === commentId);
    setComments(prev => prev.filter(c => c.id !== commentId));

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/comments/${commentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        // ✅ Notify parent to update count
        if (onCommentDeleted) onCommentDeleted();
      } else {
        // Revert on error
        if (deletedComment) {
          setComments(prev => [...prev, deletedComment]);
        }
        showToast('Failed to delete', 'error');
      }
    } catch (error) {
      // Revert on error
      if (deletedComment) {
        setComments(prev => [...prev, deletedComment]);
      }
      showToast('Failed to delete comment', 'error');
    }
  };

  const handleReport = async (reason) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/comments/${reportingId}/report`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason })
      });

      if (response.ok) {
        showToast('Report submitted. Thank you!', 'success');
      } else {
        showToast('Failed to submit report', 'error');
      }
    } catch (error) {
      showToast('Failed to submit report', 'error');
    }
  };

  // Handle keyboard
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handlePost();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-x-4 top-[10%] bottom-[10%] md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-lg md:max-h-[80vh] bg-white rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden">

        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <MessageCircle className="text-blue-600" size={20} />
            <h2 className="font-bold text-gray-900">Comments</h2>
            <span className="text-sm text-gray-500">({comments.length})</span>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
          >
            <X size={20} />
          </button>
        </div>

        {/* Benchmark info (mini) */}
        {benchmarkInfo && (
          <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex-shrink-0">
            <p className="text-xs text-gray-500 truncate">
              <span className="font-medium">{benchmarkInfo.category?.replace(/_/g, ' ')}</span>
              {' • '}
              {benchmarkInfo.prompt?.substring(0, 50)}{benchmarkInfo.prompt?.length > 50 ? '...' : ''}
            </p>
          </div>
        )}

        {/* Comments list */}
        <div className="flex-1 overflow-y-auto px-4 min-h-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="animate-spin text-blue-600" size={24} />
            </div>
          ) : comments.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <MessageCircle size={40} className="mb-3 opacity-30" />
              <p className="font-medium">No comments yet</p>
              <p className="text-sm">Be the first to share your thoughts!</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {comments.map((comment) => (
                <CommentItem
                  key={comment.id}
                  comment={comment}
                  user={user}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onReport={(id) => setReportingId(id)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Input area */}
        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          {user ? (
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Write a comment..."
                maxLength={1000}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
              <button
                onClick={handlePost}
                disabled={!newComment.trim() || posting}
                className="px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {posting ? (
                  <Loader className="animate-spin" size={18} />
                ) : (
                  <Send size={18} />
                )}
              </button>
            </div>
          ) : (
            <button
              onClick={onLoginClick}
              className="w-full py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-medium transition-colors"
            >
              Login to comment
            </button>
          )}
        </div>
      </div>

      {/* Report modal */}
      <ReportModal
        isOpen={!!reportingId}
        onClose={() => setReportingId(null)}
        onSubmit={handleReport}
      />
    </>
  );
}

export default CommentsModal;